import { createContext, useContext, useEffect, useState } from "react";
import * as goober from "goober";
import invariant from "tiny-invariant";
import { IFRAME_ID } from "@circle-react-shared/BuilderUI/Frames/constants";
import { useIFrameDocument } from "@circle-react-shared/BuilderUI/Hooks/useIFrameDocument";

const defaultGooberValue = {
  css: goober.css,
  styled: goober.styled,
} as const;

const GooberContext = createContext<typeof defaultGooberValue | null>(null);
GooberContext.displayName = "GooberContext";

/**
 * Only use for CSS styles that cannot be inlined.
 */
export const GooberContextProvider = ({
  children,
  iframeId = IFRAME_ID,
}: {
  children: React.ReactNode;
  iframeId?: string;
}) => {
  const iframeDocument = useIFrameDocument(iframeId);
  const [gooberValue, setGooberValue] =
    useState<typeof defaultGooberValue>(defaultGooberValue);

  useEffect(() => {
    if (!iframeDocument) {
      return;
    }
    const target = iframeDocument.getElementsByTagName("head")[0];
    if (target) {
      const css = goober.css.bind({ target });
      const styled = goober.styled.bind({ target });
      setGooberValue({ css, styled });
    }
  }, [iframeDocument]);
  return (
    <GooberContext.Provider value={gooberValue}>
      {children}
    </GooberContext.Provider>
  );
};

export const useGoober = () => {
  const ctxValue = useContext(GooberContext);
  invariant(ctxValue, "useGoober should be used inside GooberContextProvider");
  return ctxValue;
};
